/**
 * ZEN ERP — Siparişler Modülü
 * App.js'ten çıkarıldı.
 */
import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Eye, CreditCard, Printer, Truck, Pencil, Trash2, Check, Plus, Filter,
  FileDown, X, ChevronRight, ChevronDown, Search, RefreshCw, AlertTriangle, CheckCircle2,
  FileText, Loader2, ShoppingCart, Tag, Copy, Users, MoreHorizontal, Download, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { UICtx, AuthCtx, NavigationCtx } from "../appContexts";
import api from "../api";
import { fmt, fmtDate, ono, convertPrice, MONEY, shareUrl } from "../appUtils";
import { STATUS_LABELS } from "../mock";
import { printHtmlLabelForOrder, buildCargoLabelHTML, printViaIframe, SINEKLIK_BOYALI_KABIN_UCRETI, SINEKLIK_AHSAP_DESEN_PCT } from "../utils/pdfUtils";
import { InvoiceEditorModal } from "../components/InvoiceEditorModal";
import { QuoteBuilderPage } from "./QuoteBuilderPage";
import { QuoteViewsModal } from "../components/QuoteViewsModal";
import { StickyScrollWrapper } from "../components/StickyScrollWrapper";
import ZenPagination from "../components/ZenPagination";
import { PrintPreviewModal } from "../components/PrintPreviewModal";
import { TailorSheetButton } from "../components/TailorSheetButton";

import { CardPayModal } from "../cardPayment";
import { ModuleIcon, PName } from "../components/layout/Sidebar";
import PrintTemplateMenu from "../components/PrintTemplateMenu";
import { ItemLabelModal } from "../itemLabels";
import { ZEN_PDF_FONT_FACE } from "../quotes";

// ============ ORDERS MODULE ============
// Durum renkleri (Tailwind literal sınıflar - dinamik template'ler derlenmez)
const STATUS_CHIP = {
  sky: "bg-sky-500/25 text-sky-700 dark:text-sky-200 border-sky-500/45",
  slate: "bg-slate-500/25 text-slate-700 dark:text-slate-200 border-slate-500/45",
  indigo: "bg-indigo-500/25 text-indigo-700 dark:text-indigo-200 border-indigo-500/45",
  amber: "bg-amber-500/25 text-amber-700 dark:text-amber-200 border-amber-500/45",
  violet: "bg-violet-500/25 text-violet-700 dark:text-violet-200 border-violet-500/45",
  orange: "bg-orange-500/25 text-orange-700 dark:text-orange-200 border-orange-500/45",
  emerald: "bg-emerald-500/25 text-emerald-700 dark:text-emerald-200 border-emerald-500/45",
  green: "bg-green-500/25 text-green-700 dark:text-green-200 border-green-500/45",
  rose: "bg-rose-500/25 text-rose-700 dark:text-rose-200 border-rose-500/45",
};
const STATUS_DOT = {
  sky: "bg-sky-500", slate: "bg-slate-500", indigo: "bg-indigo-500", amber: "bg-amber-500", violet: "bg-violet-500",
  orange: "bg-orange-500", emerald: "bg-emerald-500", green: "bg-green-500", rose: "bg-rose-500", teal: "bg-teal-500",
};
const ORDER_STATUS_FLOW = ["teklif", "onay_bekliyor", "imalata_hazir", "imalatta", "hazir", "kargolanacak", "yollandi", "teslim_odeme_bekliyor", "odendi", "tamamlandi", "iptal"];
const PRODUCED_STATUSES = new Set(["imalata_hazir", "imalatta", "hazir", "kargolanacak", "yollandi", "odendi", "tamamlandi"]);

// Teslim tarihi (termin) yaklaşan/geçen siparişler için uyarı bilgisi
// Tamamlanmış (yollandı/ödendi) veya iptal siparişlerde uyarı gösterilmez
function deadlineInfo(o) {
  if (!o.deadline || ["yollandi", "odendi", "iptal"].includes(o.status)) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${o.deadline}T00:00:00`);
  if (isNaN(d.getTime())) return null;
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays > 3) return null;
  return { diffDays, overdue: diffDays < 0 };
}
function DeadlineBadge({ order, testid }) {
  const di = deadlineInfo(order);
  if (!di) return null;
  return (
    <span
      data-testid={testid}
      className={`ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap ${di.overdue ? "bg-rose-600 text-white animate-pulse" : "bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/40"}`}
    >
      {di.overdue ? `${Math.abs(di.diffDays)} gün geçti!` : di.diffDays === 0 ? "Bugün!" : `${di.diffDays} gün kaldı`}
    </span>
  );
}

// Kolon başlığı kutucuğu + altında canlı arama girişi
function ThF({ label, k, value, onChange, center, right, width, sticky, stickyLeft, stickyLast, hideClass, onResize }) {
  const stickyStyle = sticky ? { left: stickyLeft ?? 0 } : {};
  // Genişlik "tercih" olarak uygulanır — minWidth KİLİDİ YOK: pencere daralınca sütunlar
  // önce kendiliğinden daralır; yatay kaydırma yalnızca gerçekten mecbur kalınca olur
  const styleObj = width ? { ...stickyStyle, width } : stickyStyle;
  const cls = [
    center ? "text-center" : "",
    right ? "text-right" : "",
    sticky ? "col-sticky" : "",
    stickyLast ? "col-sticky-last" : "",
    hideClass || "",
  ].filter(Boolean).join(" ");
  return (
    <th className={cls + " relative"} style={styleObj}>
        <div className={`flex flex-col gap-1 w-full ${center ? "items-center" : right ? "items-end" : "items-start"}`}>
          <span className="px-2 py-0.5 rounded-md border border-slate-300 dark:border-[#2B3B5C] bg-slate-100 dark:bg-[#16213A] text-[10px] font-bold uppercase tracking-wider whitespace-nowrap">{label}</span>
        </div>
        {onResize && (
          <span
            onMouseDown={onResize(k)}
            title="Sütun genişliğini ayarlamak için sürükle"
            className="absolute right-0 top-1 bottom-1 w-[6px] cursor-col-resize rounded hover:bg-[#E5322A]/50 transition-colors"
            data-testid={`col-resize-${k}`}
          />
        )}
    </th>
  );
}

// Sipariş kalemleri tablosu (satır genişletme + detay modalında ortak)
// onItemChange verilirse ADET / KDV / B.FİYAT hücreleri DÜZENLENEBİLİR (detay ekranı)
function OrderItemsTable({ order, onItemChange, rates }) {
  const { tr, lang } = useContext(UICtx);
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const oCur = order.currency || "TL";
  const ratesObj = rates || order.rates || null;
  const isMeter = (it) => ["mt", "m", "metre", "mtül", "mtul"].includes(String(it.unit || "").toLowerCase());
  const lineOfRaw = (x) => Number(x.lineTotal) || Number(x.subtotal) || 0;
  // Kalem para birimi sipariş para biriminden farklı olabilir (EUR sipariş + TL mekanizma).
  // Kur yoksa yanlış çevrim yapıp tutarı bozmamak için düzenleme durdurulur.
  const toOrderCur = (amount, fromCur) => {
    const from = fromCur || oCur;
    const v = Number(amount) || 0;
    if (from === oCur) return v;
    if (!ratesObj || !(Number(ratesObj.eurToTl) > 0)) return null;
    return convertPrice(v, from, oCur, ratesObj);
  };
  // Bayiloji tarzı kalemlerde unitPrice/lineTotal alanı olmayabilir → subtotal'dan türet
  const dispUnit = (it) => {
    if (Number(it.unitPrice)) return Number(it.unitPrice);
    if (Number(it.subtotal)) return round2((Number(it.subtotal) || 0) / (Number(it.qty) || 1));
    return Number(it.unitPrice) || 0;
  };
  const dispTotal = (it) => Number(it.lineTotal) || Number(it.subtotal) || 0;
  const dispM2 = (it) => {
    if (it.m2Price != null && Number(it.m2Price)) return Number(it.m2Price);
    const area = Number(it.computedM2) || ((Number(it.widthCm) || 0) * (Number(it.heightCm) || 0)) / 10000;
    const base = area * (Number(it.qty) || 1);
    return area > 0 && base > 0 ? round2(dispTotal(it) / base) : null;
  };
  const commitItem = (i, field, raw) => {
    if (!onItemChange) return;
    const val = Number(String(raw ?? "").replace(",", "."));
    const prevItems = order.items || [];
    const items = prevItems.map((x) => ({ ...x }));
    const cur = items[i];
    if (!cur || !Number.isFinite(val) || val < 0) return;
    const oldLine = lineOfRaw(cur);
    const oldRate = Number(cur.kdvRate ?? 20);
    let newLine = oldLine;
    let newRate = oldRate;
    if (field === "qty") {
      const oldQty = Number(cur.qty) || 0;
      if (val === oldQty) return;
      const mult = oldQty > 0 ? oldLine / oldQty : (Number(cur.unitPrice) || 0);
      cur.qty = val;
      newLine = round2(mult * val);
    } else if (field === "kdv") {
      if (val === oldRate) return;
      newRate = val;
    } else if (field === "price") {
      const oldUnit = Number(cur.unitPrice) || 0;
      if (val === oldUnit) return;
      // çarpanı koru (m² ise m², adet ise adet — mevcut ilişki neyse o)
      const mult = oldUnit > 0 ? oldLine / oldUnit : (Number(cur.qty) || 1);
      newLine = round2(val * mult);
    }
    // K5: tüm kalemleri yeniden çevirmek yerine SADECE değişen satırın farkı
    // sipariş para birimine çevrilir. Böylece mevcut subtotal/KDV (ve varsa
    // manuel toplam) korunur, para birimi karışık siparişlerde tutar şişmez.
    const oldInOrder = toOrderCur(oldLine, cur.currency);
    const newInOrder = toOrderCur(newLine, cur.currency);
    if (oldInOrder == null || newInOrder == null) {
      toast.error("Kur bilgisi yüklenemedi — kalem düzenlenemedi");
      return;
    }
    const delta = round2(newInOrder - oldInOrder);
    // KDV farkı hem matrah hem oran değişimini kapsamalı (adet/fiyat değişince
    // KDV de değişir; yalnızca oran değişimine bakmak KDV'yi eksik bırakıyordu).
    const deltaVat = order.vatExempt
      ? 0
      : round2(newInOrder * (newRate / 100) - oldInOrder * (oldRate / 100));
    // Değişikliği kaleme uygula
    if (field === "price") cur.unitPrice = val;
    if (field === "qty" && val > 0) cur.unitPrice = round2(newLine / val);
    cur.lineTotal = newLine;
    cur.kdvRate = newRate;

    const prevSub = order.subtotal != null
      ? Number(order.subtotal)
      : prevItems.reduce((s, x) => s + (toOrderCur(lineOfRaw(x), x.currency) || 0), 0);
    const prevVat = order.vatExempt ? 0 : (order.vatAmount != null
      ? Number(order.vatAmount)
      : prevItems.reduce((s, x) => s + (toOrderCur(lineOfRaw(x), x.currency) || 0) * (Number(x.kdvRate ?? 20) / 100), 0));
    const prevTotal = order.total != null ? Number(order.total) : prevSub + prevVat;
    // Tutarlı toplamlar: kalem toplamları = ara toplam; toplam = ara toplam + KDV
    const subtotal = round2(prevSub + delta);
    const vatAmount = round2(prevVat + deltaVat);
    // Elle girilmiş (tutarsız) toplam varsa KORUNUR, yalnızca fark kadar güncellenir
    const wasConsistent = Math.abs(prevTotal - (prevSub + prevVat)) <= 0.05;
    const total = wasConsistent
      ? round2(subtotal + vatAmount)
      : round2(prevTotal + delta + (order.vatExempt ? 0 : deltaVat));
    onItemChange({ items, subtotal, vatAmount, total });
  };
  return (
    <>
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-[#22304A]">
          <th className="py-1.5 pr-2">#</th>
          <th className="pr-2">Ürün / Detay</th>
          <th className="pr-2">Ölçü</th>
          <th className="text-center pr-2">Adet</th>
          <th className="text-center pr-2">KDV</th>
          <th className="text-right pr-2">B.Fiyat</th>
          <th className="text-right">Tutar</th>
        </tr>
      </thead>
      <tbody>
        {(order.items || []).map((it, i) => (
          <tr key={i} className={`border-b border-slate-100 dark:border-[#22304A]/50 ${it.isAccessory ? "bg-amber-500/5" : ""}`}>
            <td className="py-2 pr-2 text-slate-400">{i+1}</td>
            <td className="pr-2">
              <div className="font-semibold"><PName name={it.product} /></div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {[it.urun, it.kasaTipi && `Kasa: ${it.kasaTipi}`, it.kasa && `Kasa: ${it.kasa}`, it.zincirTipi && `Zincir: ${it.zincirTipi}`, it.zincir && `Zincir: ${it.zincir}`, it.rayTipi && `Ray: ${it.rayTipi}`, it.slatGenisligi && `Slat: ${it.slatGenisligi}`, it.slatSize && `Slat ${it.slatSize} mm`, it.slatColorCode && `Renk ${it.slatColorCode}${it.slatColor && it.slatColor !== it.slatColorCode ? ` — ${it.slatColor}` : ""}`, it.fabric, it.extrasLabel && `Ekstra: ${it.extrasLabel}`, it.ralCode && `RAL ${it.ralCode}`, it.renkTipi === "boyali" && `Boyalı Kabin +${SINEKLIK_BOYALI_KABIN_UCRETI.toLocaleString("tr-TR")} TL`, it.renkTipi === "ahsap_desenli" && `Ahşap: ${it.ahsapRenk || "-"} (+%${SINEKLIK_AHSAP_DESEN_PCT})`, it.kasaRengi, it.mekanizma, it.yanKanal, it.storZigil && `Zigıl: ${it.storZigil}`, it.reduktor, it.zigilSistem && `Zıgıl: ${it.zigilSistem}`, it.ciftCamArasi && `Çift Cam Arası: ${it.ciftCamArasi}`, it.controlSide && (it.motor ? `Motor Yönü: ${it.controlSide}` : `Zincir Yönü: ${it.controlSide}`), it.motor && (it.motorLabel ? `Motor: ${it.motorLabel}` : "Motorlu"), dispM2(it) != null && `${fmt(dispM2(it), it.currency)}/m²`, it.roomName, it.note]
                  .filter(Boolean).join(" · ")}
              </div>
            </td>
            <td className="pr-2 zen-mono">{it.widthCm && it.heightCm ? `${it.widthCm}×${it.heightCm}` : "-"}</td>
            <td className="text-center pr-2 zen-mono">
              {onItemChange ? (
                <input type="number" min="0" step={isMeter(it) ? "0.01" : "1"} defaultValue={it.qty} data-testid={`detail-item-qty-${i}`}
                  onBlur={(e) => commitItem(i, "qty", e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  style={{ width: "62px" }} className="text-center zen-input !py-0.5 !text-xs" title="Adet — Enter ile kaydedilir" />
              ) : it.qty}
            </td>
            <td className="text-center pr-2 zen-mono">
              {onItemChange ? (
                <span className="inline-flex items-center gap-0.5">%<select defaultValue={it.kdvRate ?? 20} data-testid={`detail-item-kdv-${i}`}
                  onChange={(e) => commitItem(i, "kdv", e.target.value)}
                  style={{ width: "68px" }} className="text-center zen-input !py-0.5 !text-xs" title="KDV oranı">
                  {[20, 10, 0].includes(Number(it.kdvRate ?? 20)) ? null : <option value={it.kdvRate ?? 20}>{it.kdvRate ?? 20}</option>}
                  <option value={20}>20</option>
                  <option value={10}>10</option>
                  <option value={0}>0</option>
                </select></span>
              ) : `%${it.kdvRate ?? 20}`}
            </td>
            <td className="text-right pr-2 zen-mono">
              {onItemChange ? (
                <input type="number" min="0" step="0.01" defaultValue={dispUnit(it)} data-testid={`detail-item-price-${i}`}
                  onBlur={(e) => commitItem(i, "price", e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  style={{ width: "98px" }} className="text-right zen-input !py-0.5 !text-xs" title="Birim fiyat — Enter ile kaydedilir" />
              ) : fmt(dispUnit(it), it.currency)}
            </td>
            <td className="text-right zen-mono font-bold">{fmt(dispTotal(it), it.currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {/* BELGE TAKİP - durum geçmişi zaman çizelgesi */}
    <div className="mt-3 pt-2 border-t border-slate-200 dark:border-[#22304A]" data-testid="status-history">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Belge Takip</div>
      <div className="space-y-1">
        {(order.statusHistory && order.statusHistory.length > 0
          ? order.statusHistory
          : [{ status: order.status, date: order.createdAt, note: "" }]
        ).map((h, i) => {
          const hs = STATUS_LABELS[h.status] || { tr: h.status, color: "slate" };
          return (
            <div key={i} className="flex items-center gap-2 text-[11px]" data-testid={`status-history-row-${i}`}>
              <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[hs.color] || "bg-slate-500"}`} />
              <span className="zen-mono text-slate-500 shrink-0">{h.date ? new Date(h.date).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}</span>
              <span className="font-semibold">{hs.tr}</span>
              {h.note && <span className="text-slate-500 italic truncate">— {h.note}</span>}
              {h.by && <span className="text-slate-400 text-[10px] shrink-0">({h.by})</span>}
            </div>
          );
        })}
      </div>
    </div>
  </>
  );
}

// Sipariş detay popup'ı
// ─── Finansal Düzenle Modalı ──────────────────────────────────────────────────
function FinancialEditModal({ order, onClose, onSaved }) {
  const [total, setTotal] = useState(String(order.total ?? ""));
  const [vatExempt, setVatExempt] = useState(order.vatExempt || false);
  const [kdvRate, setKdvRate] = useState(order.kdvRate ?? order.vatRate ?? 20);
  const [recalcBalance, setRecalcBalance] = useState(false);
  const [saving, setSaving] = useState(false);

  const origTotal = parseFloat(order.total) || 0;
  const newTotal  = parseFloat(total) || 0;
  const diff = newTotal - origTotal;

  const handleSave = async () => {
    if (!total.trim() || isNaN(parseFloat(total))) { toast.error("Geçerli bir tutar girin"); return; }
    setSaving(true);
    try {
      await api.put(`/orders/${order.id}`, {
        total: newTotal,
        vatExempt,
        kdvRate: vatExempt ? 0 : Number(kdvRate),
        // Manuel finansal özet: toplam, kalem birim fiyatlarına / m² fiyatlarına
        // orantılı dağıtılır (kalem toplamları girilen rakama BİREBİR uyar)
        distributeToItems: true,
      });
      if (recalcBalance && order.customerId) {
        await api.post(`/customers/${order.customerId}/recalculate-balance`);
        toast.success("Sipariş ve müşteri bakiyesi güncellendi");
      } else {
        toast.success("Sipariş güncellendi");
      }
      onSaved();
    } catch (e) {
      toast.error("Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="financial-edit-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#0C1220] rounded-2xl shadow-2xl w-full max-w-sm border border-slate-200 dark:border-[#1e2d42]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-[#1e2d42]">
          <div>
            <h3 className="text-base font-black text-slate-900 dark:text-white">Finansal Düzenle</h3>
            <p className="text-xs text-slate-400 mt-0.5">{ono(order)} · {order.customerName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-[#1e2d42] text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Toplam Tutar */}
          <div>
            <label className="text-xs font-bold text-slate-600 dark:text-slate-400 mb-1.5 block">
              Toplam Tutar <span className="text-slate-400 font-normal">({order.currency || "EUR"})</span>
            </label>
            <div className="relative flex items-center">
              <input
                type="number"
                step="0.01"
                min="0"
                value={total}
                onChange={e => setTotal(e.target.value)}
                className="zen-input w-full text-right text-base font-bold pr-14"
                data-testid="fin-edit-total-input"
              />
              <span className="absolute right-3 text-sm font-bold text-slate-400 pointer-events-none">{order.currency || "EUR"}</span>
            </div>
            {Math.abs(diff) > 0.01 && (
              <p className={`text-xs mt-1 font-semibold ${diff > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`}>
                {diff > 0 ? "+" : ""}{diff.toFixed(2)} {order.currency || "EUR"} değişim
              </p>
            )}
            {(order.items || []).length > 0 && (
              <p className="text-[10px] text-slate-400 mt-1.5 leading-relaxed" data-testid="fin-edit-distribute-hint">
                Toplam; kalem birim fiyatlarına ve m² fiyatlarına ölçüt oranında dağıtılır —
                kalem toplamları bu rakama birebir uyar.
              </p>
            )}
          </div>

          {/* KDV Ayarları */}
          <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-[#131B2E] border border-slate-200 dark:border-[#1e2d42] space-y-3">
            <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">KDV Ayarları</div>

            {/* KDV Muaf toggle */}
            <button
              type="button"
              onClick={() => setVatExempt(v => !v)}
              className="flex items-center gap-3 w-full text-left"
              data-testid="fin-edit-vat-exempt-toggle">
              <div className={`w-9 h-5 rounded-full transition-colors shrink-0 ${vatExempt ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"} relative`}>
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${vatExempt ? "translate-x-4" : "translate-x-0"}`} />
              </div>
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">KDV Muaf</span>
              {vatExempt && <span className="ml-auto text-xs text-emerald-600 dark:text-emerald-400 font-bold">%0</span>}
            </button>

            {/* KDV Oranı seçimi — sadece muaf değilse */}
            {!vatExempt && (
              <div>
                <label className="text-xs text-slate-500 mb-1.5 block">KDV Oranı</label>
                <div className="flex gap-2">
                  {[0, 10, 20].map(r => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setKdvRate(r)}
                      className={`flex-1 py-1.5 rounded-lg text-sm font-bold border transition-all ${Number(kdvRate) === r ? "bg-[#E5322A] border-[#E5322A] text-white shadow-sm" : "border-slate-200 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:border-[#E5322A]/50 bg-white dark:bg-[#0C1220]"}`}
                      data-testid={`fin-edit-kdv-rate-${r}`}>
                      %{r}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Müşteri bakiyesi güncelle */}
          {Math.abs(diff) > 0.01 && order.customerId && (
            <label className="flex items-start gap-2.5 cursor-pointer" data-testid="fin-edit-recalc-balance">
              <input
                type="checkbox"
                checked={recalcBalance}
                onChange={e => setRecalcBalance(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[#E5322A] shrink-0"
              />
              <span className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Müşteri bakiyesini yeniden hesapla
                <span className={`ml-1 font-bold ${diff > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`}>
                  ({diff > 0 ? "+" : ""}{diff.toFixed(2)} {order.currency || "EUR"})
                </span>
              </span>
            </label>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 border-t border-slate-200 dark:border-[#1e2d42]">
          <button onClick={onClose} className="zen-btn-ghost flex-1">İptal</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="zen-btn-primary flex-1"
            data-testid="fin-edit-save-btn">
            {saving ? "Kaydediliyor…" : "Kaydet"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sipariş Detay Modalı ───────────────────────────────────────────────────
// TEK SİPARİŞ DETAY MODALI — hem Siparişler hem Dashboard kullanır (birleştirildi, 22.10.2026)
// Aksiyonlar isteğe bağlıdır: verilmeyen buton görünmez (örn. Dashboard'da finansal düzenleme yok)
export function OrderDetailModal({ order, onClose, onEdit, onStatusChange, onFinancialEdit, onPayment }) {
  const { tr } = useContext(UICtx);
  const [payments, setPayments] = useState([]);
  const [sewingJobs, setSewingJobs] = useState([]);
  const [showLabels, setShowLabels] = useState(false);
  const [localStatus, setLocalStatus] = useState(order?.status);
  // K5: kalem düzenlemede para birimi çevrimi için kur gerekir. Siparişte kur
  // yoksa /rates'ten alınır (canlı kayıtlarda order.rates çoğunlukla yok).
  const [rates, setRates] = useState(order?.rates || null);
  // Kalem düzenleme (ADET / KDV / B.FİYAT) — toplamlar tutarlı yeniden hesaplanır;
  // backend tutar değişikliğinde cari bakiye farkını OTOMATİK işler (muhasebe bütünlüğü)
  const [localFin, setLocalFin] = useState({ items: order?.items || [], subtotal: order?.subtotal, vatAmount: order?.vatAmount, total: order?.total });
  const handleItemChange = async (patch) => {
    try {
      await api.put(`/orders/${order.id}`, patch);
      setLocalFin((d) => ({ ...d, ...patch }));
      toast.success("Kalem güncellendi — toplamlar yeniden hesaplandı, cari bakiye işlendi");
    } catch (e) {
      toast.error("Güncellenemedi — değişiklik kaydedilmedi");
    }
  };

  useEffect(() => {
    if (!order) return;
    setLocalStatus(order.status);
    setRates(order.rates || null);
    if (!order.rates || !(Number(order.rates.eurToTl) > 0)) {
      api.get("/rates").then(r => setRates(r.data || null)).catch(() => {});
    }
    api.get(`/payments?orderId=${order.id}`).then(r => setPayments(r.data || [])).catch(() => {});
    api.get(`/workshop-assignments?orderId=${order.id}`).then(r => setSewingJobs(r.data || [])).catch(() => {});
  }, [order?.id]);

  if (!order) return null;
  const stat  = STATUS_LABELS[localStatus] || STATUS_LABELS[order.status] || { tr: order.status, color: "slate" };
  const rc    = STATUS_CHIP[stat.color] || STATUS_CHIP.slate;
  const remaining  = ((localFin.total ?? order.total) || 0) - (order.paidAmount || 0);
  // KDV: siparişin GERÇEK alanları esas alınır (24.10.2026 — sabit /1.2 varsayımı kaldırıldı;
  // %10 / KDV istisnası siparişlerde artık yanlış rakam gösterilmez)
  // Düzenleme sonrası: yerel finansal değerler (kalem değişince canlı güncellenir)
  const finTotal = localFin.total ?? order.total ?? 0;
  const finVat   = localFin.vatAmount ?? order.vatAmount;
  const kdvAmount  = finVat != null ? (Number(finVat) || 0)
                   : (order.vatExempt ? 0 : finTotal - finTotal / 1.2);
  const netAmount  = finTotal - kdvAmount;

  const handleStatusChange = (newStatus) => {
    if (!onStatusChange) return; // opsiyonel aksiyon: verilmediyse (örn. Dashboard) durum salt okunur
    if (newStatus === localStatus) return;
    setLocalStatus(newStatus);
    onStatusChange(order, newStatus);
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex" data-testid="order-detail-modal">
      {/* TAM SAYFA sipariş detayı — diğer tam sayfa görünümlerle aynı dil */}
      <div className="relative w-full h-full flex flex-col zen-print-center zen-anim-up zen-order-detail">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 dark:border-[#1e2d42] shrink-0">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-xl font-extrabold zen-mono text-[#E5322A]">{ono(order)}</span>
              <span className={`zen-chip ${rc}`}>{stat.tr}</span>
              {order.deadline && <DeadlineBadge order={order} testid="deadline-detail" />}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              <span className="font-semibold">{order.customerName}</span>
              {order.projectName && <span className="text-slate-400"> · {order.projectName}</span>}
              <span className="text-slate-400"> · {new Date(order.createdAt || Date.now()).toLocaleString("tr-TR",{day:"2-digit",month:"2-digit",year:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
              {order.deadline && <span className="text-slate-400"> · Termin: {fmtDate(order.deadline)}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(order.items || []).some(it => {
              const s = ((it.product || "") + (it.groupSlug || "")).toLowerCase();
              return s.includes("zip") || s.includes("sky");
            }) && (
              <button onClick={() => setShowLabels(true)} className="zen-btn-ghost" data-testid="order-detail-label-btn">
                <Tag className="w-3.5 h-3.5" /> Etiket Yaz
              </button>
            )}
            {onEdit && (
              <button onClick={() => { onEdit(order); }} className="zen-btn-primary" data-testid="order-detail-edit-btn">
                <Pencil className="w-3.5 h-3.5" /> Düzenle
              </button>
            )}
            {onPayment && (
              <button onClick={() => onPayment(order)} className="zen-btn-primary !bg-emerald-600 hover:!bg-emerald-700 !border-transparent" data-testid="order-detail-payment-btn">
                <CreditCard className="w-3.5 h-3.5" /> Ödeme Ekle
              </button>
            )}
            {onFinancialEdit && (
              <button onClick={() => onFinancialEdit(order)} className="zen-btn-ghost !border-amber-400 !text-amber-600 dark:!text-amber-400 hover:!bg-amber-50 dark:hover:!bg-amber-500/10" data-testid="order-detail-financial-edit-btn">
                <CreditCard className="w-3.5 h-3.5" /> Finansal
              </button>
            )}
            {/* GERİ OKU — sipariş listesine döner (X yok, diğer sayfalarla aynı) */}
            <button onClick={onClose} data-testid="order-detail-close" title="Sipariş listesine dön" className="zen-btn-ghost">
              <ArrowLeft className="w-4 h-4" /> Siparişlere Dön
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Left: sipariş kalemleri + notlar + dikiş */}
          <div className="flex-1 overflow-y-auto px-6 py-5 border-r border-slate-200 dark:border-[#1e2d42] space-y-5">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Sipariş Kalemleri</div>
              <OrderItemsTable order={{ ...order, ...localFin }} onItemChange={handleItemChange} rates={rates} />
            </div>

            {order.notes && (
              <div className="text-xs text-slate-500 p-3 rounded-xl bg-slate-50 dark:bg-[#131B2E] border border-slate-200 dark:border-[#1e2d42]">
                <span className="font-bold text-slate-700 dark:text-slate-300">Not:</span> {order.notes}
              </div>
            )}

            {sewingJobs.length > 0 && (
              <div data-testid="sewing-jobs-section">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Dikiş İşçiliği
                </div>
                <div className="space-y-1">
                  {sewingJobs.map((sj, i) => (
                    <div key={sj.id || i} className="flex items-start justify-between gap-2 text-[11px] py-1 border-b border-slate-100 dark:border-[#1e2d42]/50 last:border-0" data-testid={`sewing-job-row-${i}`}>
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold">{sj.workshopName || "?"}</span>
                        <span className="text-slate-400 mx-1">—</span>
                        <span className="text-slate-500">{(sj.items || []).slice(0,2).map((it,ii) => (<span key={ii}>{it.productType} ({it.qty} {it.unit}){ii < Math.min(sj.items.length,2)-1?", ":""}</span>))}{(sj.items||[]).length>2&&<span className="text-slate-400"> +{sj.items.length-2}</span>}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${sj.status==="odendi"?"bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400":sj.status==="tamamlandi"?"bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400":"bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"}`}>{sj.status==="odendi"?"Ödendi":sj.status==="tamamlandi"?"Tamamlandı":"Bekliyor"}</span>
                        <span className="zen-mono font-bold text-amber-600 dark:text-amber-400">{(sj.totalAmount||0).toLocaleString("tr-TR")} ₺</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-[11px] font-bold pt-2 mt-1 border-t border-slate-100 dark:border-[#1e2d42]/50">
                  <span className="text-slate-500">Toplam Dikiş</span>
                  <span className="zen-mono text-amber-600 dark:text-amber-400">{sewingJobs.reduce((s,j)=>s+(j.totalAmount||0),0).toLocaleString("tr-TR")} ₺</span>
                </div>
              </div>
            )}
          </div>

          {/* Right sidebar */}
          <div className="w-72 shrink-0 overflow-y-auto px-5 py-5 space-y-4">

            {/* Durum Değiştir */}
            <div className="zen-panel p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2.5">Sipariş Durumu</div>
              <select
                data-testid="order-detail-status-select"
                value={localStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                className="zen-input w-full text-sm font-semibold"
              >
                {ORDER_STATUS_FLOW.map(k => {
                  const s = STATUS_LABELS[k];
                  return <option key={k} value={k}>{s.tr}</option>;
                })}
              </select>
              <p className="text-[10px] text-slate-400 mt-1.5">Değiştirince onay notu istenebilir</p>
            </div>

            {/* Finansal Özet */}
            <div className="zen-panel p-4 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Finansal Özet</div>
                {onFinancialEdit && (
                <button onClick={() => onFinancialEdit(order)} className="p-1 rounded hover:bg-amber-50 dark:hover:bg-amber-500/10 text-amber-500 hover:text-amber-600 transition" title="Finansal Düzenle" data-testid="order-detail-fin-edit-icon">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                )}
              </div>
              {order.discountPercent > 0 && (
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Ara Toplam (indirim öncesi)</span>
                  <span className="zen-mono line-through">{fmt(order.subtotal, order.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs text-slate-600 dark:text-slate-300">
                <span>KDV Hariç</span>
                <span className="zen-mono">{fmt(netAmount, order.currency)}</span>
              </div>
              <div className="flex justify-between text-xs text-slate-600 dark:text-slate-300">
                <span>{netAmount > 0 && kdvAmount > 0 ? `KDV (%${Math.round((kdvAmount / netAmount) * 100)})` : "KDV"}</span>
                <span className="zen-mono">{fmt(kdvAmount, order.currency)}</span>
              </div>
              <div className="flex justify-between text-sm font-extrabold pt-2 border-t border-slate-200 dark:border-[#1e2d42]">
                <span>TOPLAM</span>
                <span className="zen-mono" data-testid="order-detail-total">{fmt(finTotal, order.currency)}</span>
              </div>
              {remaining > 0.01 && (
                <div className="flex justify-between text-xs font-bold text-amber-500">
                  <span>Kalan Ödeme</span>
                  <span className="zen-mono" data-testid="payment-history-remaining">{fmt(remaining, order.currency)}</span>
                </div>
              )}
            </div>

            {/* Ödeme Geçmişi */}
            <div className="zen-panel p-4" data-testid="payment-history">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Ödeme Geçmişi</div>
              {payments.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Henüz ödeme yok</p>
              ) : (
                <div className="space-y-1.5">
                  {payments.map((p, i) => (
                    <div key={p.id||i} className="flex items-center gap-2 text-[11px]" data-testid={`payment-history-row-${i}`}>
                      <span className="w-2 h-2 rounded-full shrink-0 bg-emerald-500" />
                      <span className="zen-mono text-slate-400 shrink-0">{p.date?new Date(`${p.date}T00:00:00`).toLocaleDateString("tr-TR",{day:"2-digit",month:"2-digit",year:"numeric"}):"-"}</span>
                      <span className="zen-mono font-bold text-emerald-600 dark:text-emerald-400">{fmt(p.amount, p.currency||order.currency)}</span>
                      {p.method && <span className="text-slate-500">· {p.method}</span>}
                    </div>
                  ))}
                  <div className="flex justify-between text-[11px] font-bold pt-1.5 border-t border-slate-100 dark:border-[#1e2d42]/50">
                    <span>Toplam Ödenen</span>
                    <span className="zen-mono text-emerald-600 dark:text-emerald-400" data-testid="payment-history-total-paid">{fmt(order.paidAmount||0, order.currency)}</span>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
    {showLabels && <ItemLabelModal order={order} onClose={() => setShowLabels(false)} />}
    </>
  );
}

// ─── Kargo Etiketi HTML Üretici ──────────────────────────────────────────────
// buildCargoLabelHTML pdfUtils.js'den import edildi

export function OrdersModule({ editingOrder, setEditingOrder }) {
  const { tr, lang } = useContext(UICtx);
  const { user } = useContext(AuthCtx);
  const { setTab: setGlobalTab } = useContext(NavigationCtx);
  const isDealer = user?.role === "dealer";
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const [showNewOrderQuote, setShowNewOrderQuote] = useState(false); // yeni sipariş = QuoteBuilder
  const [showEditOrderQuote, setShowEditOrderQuote] = useState(false); // sipariş düzenleme = QuoteBuilder
  const [printOrder, setPrintOrder] = useState(null);
  const [printDocType, setPrintDocType] = useState("teklif");
  const [detailOrder, setDetailOrder] = useState(null);
  const [financialEditOrder, setFinancialEditOrder] = useState(null); // finansal düzenleme modalı
  const [expandedId, setExpandedId] = useState(null);
  const [statusMenu, setStatusMenu] = useState(null); // { order, top, left }
  const [selected, setSelected] = useState(new Set()); // toplu seçim
  const [exportingPdf, setExportingPdf] = useState(false);
  const [quickMenu, setQuickMenu] = useState(null);          // { order, top, left }
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  useEffect(() => { setPage(1); }, [q, filter]);
  useEffect(() => {
    if (!statusMenu) return;
    const onDown = (e) => {
      if (e.target.closest('[data-testid^="status-dot-"]') || e.target.closest('[data-testid^="status-menu-"]')) return;
      setStatusMenu(null);
    };
    const onEsc = (e) => { if (e.key === "Escape") setStatusMenu(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", () => setStatusMenu(null), { once: true, capture: true });
    document.addEventListener("scroll", () => setStatusMenu(null), { once: true, capture: true });
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
  }, [statusMenu]);
  // quickMenu kapatma
  useEffect(() => {
    if (!quickMenu) return;
    const onDown = (e) => {
      if (e.target.closest('[data-testid^="quick-menu"]')) return;
      setQuickMenu(null);
    };
    const onEsc = (e) => { if (e.key === "Escape") setQuickMenu(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
  }, [quickMenu]);
  const [notePrompt, setNotePrompt] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [customStatuses, setCustomStatuses] = useState([]);
  const [views, setViews] = useState({});
  const [seenOrder, setSeenOrder] = useState(null);
  const [cardPay, setCardPay] = useState(null);
  const [cargoLabelSettings, setCargoLabelSettings] = useState({ w: 100, h: 75, fontSize: 9, showBorder: true });
  const [orderInvoices, setOrderInvoices] = useState({});   // orderId → invoice
  const [invoiceViewer, setInvoiceViewer] = useState(null);  // invoice object
  const [invoiceEditor, setInvoiceEditor] = useState(null);  // order object (fatura editörü)
  const [statusColorOverrides, setStatusColorOverrides] = useState({}); // { statusKey: colorName }

  // Özelleştirilmiş durum renklerini yükle
  useEffect(() => {
    api.get("/settings", { params: { category: "order_statuses" } }).then(({ data }) => {
      const overrides = {};
      (data || []).forEach(r => { if (r.key.endsWith("_color")) overrides[r.key.replace("_color", "")] = r.value; });
      setStatusColorOverrides(overrides);
    }).catch(() => {});
  }, []);

  // Durum rengini çöz (özelleştirilmiş > varsayılan)
  const resolveColor = (statusKey) => statusColorOverrides[statusKey] || (STATUS_LABELS[statusKey] || {}).color || "slate";

  useEffect(() => {
    api.get("/settings", { params: { category: "printer_settings" } }).then(({ data }) => {
      const map = {};
      (data || []).forEach(r => { map[r.key] = r.value; });
      if (map["tpl_kargo_etiketi"]) {
        try { setCargoLabelSettings(prev => ({ ...prev, ...JSON.parse(map["tpl_kargo_etiketi"]) })); } catch {}
      }
    }).catch(() => {});
  }, []);

  const printCargo = async (order) => {
    // Önce HTML etiket şablonu denemesi, yoksa eski sabit şablon
    try {
      await printHtmlLabelForOrder(order, "kargo");
    } catch (e) {
      // Fallback — gizli iframe ile yazdır (yeni sekme açılmaz)
      const html = buildCargoLabelHTML(order, cargoLabelSettings);
      const iframe = document.createElement("iframe");
      iframe.setAttribute("data-zen-print-frame", "1");
      Object.assign(iframe.style, { position:"fixed", top:"-9999px", left:"-9999px", width:"1px", height:"1px", opacity:"0", border:"none" });
      document.body.appendChild(iframe);
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      doc.open(); doc.write(html); doc.close();
      setTimeout(() => {
        try { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); } catch(_) {}
        setTimeout(() => { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); }, 3000);
      }, 400);
    }
  };

  const load = async () => {
    const [r, s] = await Promise.all([
      api.get("/orders"),
      api.get("/settings?category=sales_statuses"),
    ]);
    setOrders(r.data);
    setCustomStatuses((s.data || []).filter(x => x.active !== false));
    // Fatura haritası (orderId → fatura)
    try {
      const { data: invData } = await api.get("/invoices");
      const invMap = {};
      (invData || []).forEach(inv => { if (inv.orderId && !invMap[inv.orderId]) invMap[inv.orderId] = inv; });
      setOrderInvoices(invMap);
    } catch {}
    try {
      const { data: v } = await api.get("/share-views", { params: { refType: "order" } });
      setViews(v || {});
    } catch (e) { /* takip verisi opsiyonel */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (editingOrder) {
      // TEK MOTOR: CRM lead (_new) ve mevcut sipariş düzenleme AYNI forma gider
      // (eski OrderModal kaldırıldı — tek sipariş motoru: QuoteBuilderPage/saveAsOrder)
      setShowEditOrderQuote(true);
    }
  }, [editingOrder]);

  const [colFilters, setColFilters] = useState({ sira: "", orderNo: "", status: "", cari: "", proje: "", forms: "", adet: "" });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // SÜTUN GENİŞLİKLERİ: sürükleyerek AYARLANIR, "Sütunları Kaydet" ile KALICI olur
  const [colWidths, setColWidths] = useState(() => {
    try { return JSON.parse(localStorage.getItem("zen_orders_colwidths") || "{}"); } catch { return {}; }
  });
  // Tablo düzeni — NİHAI MATEMATİK:
  // • İçeriği kesin ölçülü sütunlar (sıra, sipariş no, form, adet, tutar, işlem) PX'tir: ASLA kesilmez.
  // • DURUM + CARİ ekranın %44'ünü paylaşır — oran sizin ayarınızdan gelir (tavan/asgari ile).
  // • REFERANS bilinçli genişlik almaz: kalan alanın tamamını o emer.
  // • 900px altı REFERANS/ADET/rozet, 1100px altı FORM gizlenir → hiçbir ekranda taşma olmaz.
  const clamp = (v, min, max) => Math.min(Math.max(Number(v) || 0, min), max);
  const px = (v) => `${clamp(v, 40, 260)}px`;
  const stBase = clamp(colWidths.status ?? 175, 140, 480);
  const caBase = clamp(colWidths.cari ?? 260, 160, 700);
  const scShare = 44; // durum+cari'nin ekran payı (%)
  const statusPct = `${Math.round((stBase / (stBase + caBase)) * scShare * 10) / 10}%`;
  const cariPct = `${Math.round((caBase / (stBase + caBase)) * scShare * 10) / 10}%`;
  const colW = {
    sira: px(colWidths.sira ?? 92),
    orderNo: px(colWidths.orderNo ?? 82),
    status: statusPct,
    cari: cariPct,
    forms: px(colWidths.forms ?? 86),
    adet: px(colWidths.adet ?? 46),
    tutar: px(colWidths.tutar ?? 98),
    islem: px(colWidths.islem ?? 64),
  };
  const [widthsDirty, setWidthsDirty] = useState(false);
  const saveColWidths = () => {
    try {
      localStorage.setItem("zen_orders_colwidths", JSON.stringify(colWidths));
      setWidthsDirty(false);
      toast.success("Sütun düzeni kaydedildi — bir dahaki açılışta bu düzen gelir");
    } catch { toast.error("Tarayıcı depolaması kapalı olabilir"); }
  };
  const resetColWidths = () => {
    try { localStorage.removeItem("zen_orders_colwidths"); } catch { /* yoksay */ }
    setColWidths({});
    setWidthsDirty(false);
    toast.success("Sütun genişlikleri varsayılana döndü");
  };
  const startResize = (key) => (e) => {
    e.preventDefault();
    const th = e.target.closest("th");
    const startW = th ? th.getBoundingClientRect().width : 120;
    const startX = e.clientX;
    const onMove = (ev) => {
      const w = Math.max(56, Math.round(startW + (ev.clientX - startX)));
      setColWidths((p) => ({ ...p, [key]: w }));
      setWidthsDirty(true);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const setCF = (k, v) => setColFilters((p) => ({ ...p, [k]: v }));
  const trLower = (s) => (s || "").toLocaleLowerCase("tr");

  const filtered = orders.filter((o) => {
    if (dateFrom && (o.createdAt || "").slice(0, 10) < dateFrom) return false;
    if (dateTo && (o.createdAt || "").slice(0, 10) > dateTo) return false;
    if (filter !== "all" && o.status !== filter) return false;
    if (q) {
      // EVRENSEL ARAMA (24.10.2026): tek kutu — sipariş no, müşteri, proje, form, not, durum
      const hay = [
        o.customerName, ono(o), o.id, o.projectName, o.notes,
        (STATUS_LABELS[o.status] || {}).tr, o.status,
        (o.items || []).map((it) => [it.product, it.fabric, it.groupSlug].filter(Boolean).join(" ")).join(" "),
      ].filter(Boolean).join(" ").toLocaleLowerCase("tr");
      if (!hay.includes(q.toLocaleLowerCase("tr"))) return false;
    }
    if (colFilters.orderNo && !trLower(ono(o)).includes(trLower(colFilters.orderNo))) return false;
    if (colFilters.status) {
      const statTr = (STATUS_LABELS[o.status] || { tr: o.status }).tr;
      if (!trLower(statTr).includes(trLower(colFilters.status))) return false;
    }
    if (colFilters.cari) {
      const qf = trLower(colFilters.cari);
      const words = trLower(o.customerName).split(/\s+/);
      if (!words.some((w) => w.startsWith(qf))) return false;
    }
    if (colFilters.proje && !trLower(o.projectName || "").includes(trLower(colFilters.proje))) return false;
    if (colFilters.forms) {
      const formsText = trLower((o.items || []).map((it) => it.product || "").join(" "));
      if (!formsText.includes(trLower(colFilters.forms))) return false;
    }
    if (colFilters.adet) {
      const countable = (o.items || []).filter((it) => !it.isAccessory);
      const parts = countable.reduce((s, it) => s + (Number(it.qty) || 0), 0) || countable.length;
      if (String(parts) !== colFilters.adet.trim()) return false;
    }
    return true;
  }).filter((o, idx) => !colFilters.sira || String(idx + 1) === colFilters.sira.trim());
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const pageMaxTotal = Math.max(1, ...paginated.map((o) => Number(o.total) || 0));

  const applyStatusChange = async () => {
    const { order, status } = notePrompt;
    try {
      if (order.status === "teklif" && status !== "iptal" && status !== "teklif") {
        // Teklif onaylanıyor: cari bakiye işlensin
        await api.post(`/orders/${order.id}/approve`, { note: status === "imalatta" ? noteText : "" });
        if (status !== "imalatta") await api.put(`/orders/${order.id}`, { status, statusNote: noteText });
      } else if (status === "iptal") {
        await api.post(`/orders/${order.id}/cancel`, { note: noteText });
      } else if (status === "odendi") {
        // Elle "Ödendi" seçilirse kalan tutar tahsilat olarak kaydedilir (cari/ödeme alanları senkron kalır)
        const remain = (order.total || 0) - (order.paidAmount || 0);
        if (remain > 0.01) {
          await api.post("/payments", {
            customerId: order.customerId, orderId: order.id, amount: remain, currency: order.currency,
            method: "manuel", note: `${ono(order)} - durum değişikliğiyle ödendi işaretlendi${noteText ? " · " + noteText : ""}`,
          });
        }
        await api.put(`/orders/${order.id}`, { status, statusNote: noteText });
      } else {
        await api.put(`/orders/${order.id}`, { status, statusNote: noteText });
      }
      toast.success(`Durum güncellendi: ${(STATUS_LABELS[status] || { tr: status }).tr}`, {
        ...(status === "kargolanacak" ? {
          duration: 9000,
          action: {
            label: "🏷 Kargo Etiketi Yazdır",
            onClick: () => printHtmlLabelForOrder(order, "kargo"),
          },
        } : {}),
      });

      // 🎯 Tamamlandı Otomatik Tetikleyici
      // "Yollandı" durumuna geçince VE sipariş tam ödenmişse öneri göster
      if (status === "yollandi") {
        const paidAmount = order.paidAmount || 0;
        const totalAmount = order.total || 0;
        if (totalAmount > 0 && paidAmount >= totalAmount - 0.01) {
          setTimeout(() => {
            toast.info(`${ono(order)} tam ödenmiş ve yollandı!`, {
              description: "Bu siparişi 'Tamamlandı' olarak işaretlemek ister misiniz?",
              duration: 12000,
              action: {
                label: "Tamamlandı'ya Geç",
                onClick: async () => {
                  try {
                    await api.put(`/orders/${order.id}`, { status: "tamamlandi" });
                    toast.success("Sipariş Tamamlandı olarak işaretlendi");
                    load();
                  } catch (e) {
                    toast.error("Durum güncellenemedi");
                  }
                },
              },
            });
          }, 1500);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Durum güncellenemedi");
    }
    setNotePrompt(null); setNoteText("");
    load();
    // Detay paneli açıksa durumu anında yansıt
    if (detailOrder?.id === order.id) {
      setDetailOrder(prev => prev ? { ...prev, status } : null);
    }
  };

  const del = async (o) => {
    if (!window.confirm(`${ono(o)} arşive taşınsın mı? Arşiv bölümünden geri alabilirsiniz.`)) return;
    await api.delete(`/orders/${o.id}`);
    toast.success("Arşive taşındı", { description: "Arşiv sayfasından geri yükleyebilirsiniz." });
    load();
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(o => o.id)));
  };

  const exportBulkPDF = async (mode = "sade") => {
    const sel = filtered.filter(o => selected.has(o.id));
    if (!sel.length) { toast.error("Sipariş seçilmedi"); return; }
    setExportingPdf(true);
    try {
      const now = new Date().toLocaleString("tr-TR");
      const rows = sel.map((o, i) => {
        const stat = STATUS_LABELS[o.status] || { tr: o.status };
        const totalStr = `${(o.totalAmount || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ${o.currency || "TL"}`;
        if (mode === "sade") {
          return `<tr>
            <td>${i + 1}</td><td><b>${ono(o)}</b></td><td>${o.customerName || ""}</td>
            <td>${stat.tr}</td><td>${o.projectName || ""}</td><td>${totalStr}</td>
            <td>${o.createdAt ? new Date(o.createdAt).toLocaleDateString("tr-TR") : ""}</td>
          </tr>`;
        }
        const itemRows = (o.items || []).filter(it => !it.isAccessory).map(it =>
          `<tr><td style="padding-left:16px;color:#555">${it.product || ""}</td><td>${it.qty || 1} ${it.unit || "ad"}</td><td>${(it.unitPrice || 0).toLocaleString("tr-TR", {maximumFractionDigits:2})} ${o.currency || "TL"}</td></tr>`
        ).join("");
        return `<tr><td colspan="7" style="padding:0;border-bottom:2px solid #C42821">
          <table width="100%" style="border-collapse:collapse;font-size:9pt">
            <tr style="background:#fff5f5"><td colspan="3" style="padding:6px 10px;font-weight:bold">
              ${i+1}. ${ono(o)} &nbsp;·&nbsp; ${o.customerName || ""} &nbsp;·&nbsp; <span style="color:#C42821">${totalStr}</span>
              <span style="float:right;color:#888;font-size:8pt">${stat.tr} &nbsp; ${o.createdAt ? new Date(o.createdAt).toLocaleDateString("tr-TR") : ""}</span>
            </td></tr>
            ${o.projectName ? `<tr><td colspan="3" style="padding:2px 10px 4px;color:#777;font-size:8pt">Proje: ${o.projectName}</td></tr>` : ""}
            ${itemRows ? `<tr><th style="padding:3px 10px;background:#f0f0f0;font-size:8pt">Ürün</th><th style="background:#f0f0f0;font-size:8pt">Adet</th><th style="background:#f0f0f0;font-size:8pt">Birim Fiyat</th></tr>${itemRows}` : ""}
          </table>
        </td></tr>`;
      }).join("");

      const thead = mode === "sade"
        ? `<tr><th>#</th><th>Sipariş No</th><th>Cari</th><th>Durum</th><th>Proje</th><th>Tutar</th><th>Tarih</th></tr>`
        : `<tr><th colspan="7">Siparişler</th></tr>`;

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Sipariş Listesi</title>
<style>${ZEN_PDF_FONT_FACE}body{font-family:ZenSans,Arial,sans-serif;font-size:9pt;margin:20px}h1{font-size:14pt;color:#C42821;margin-bottom:3px}.meta{font-size:8pt;color:#888;margin-bottom:10px}table{width:100%;border-collapse:collapse}th{background:#C42821;color:#fff;padding:5px 8px;text-align:left;font-size:9pt}td{border-bottom:1px solid #eee;padding:4px 8px}tr:nth-child(even) td{background:#fafafa}</style>
</head><body>
<h1>Sipariş Listesi — ${mode === "sade" ? "Özet" : "Detaylı"}</h1>
<div class="meta">Oluşturulma: ${now} · Seçili: ${sel.length} sipariş</div>
<table><thead>${thead}</thead><tbody>${rows}</tbody></table>
</body></html>`;

      const { data } = await api.post("/share/html-pdf", { html, filename: `Siparisler-${Date.now()}.pdf` });
      printViaIframe(shareUrl(data.path), true);
      toast.success(`${sel.length} sipariş PDF'e aktarıldı`);
    } catch (e) {
      toast.error("PDF oluşturulamadı");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="space-y-4 zen-anim-up zen-orders-page zen-hc-workspace rounded-2xl p-1 sm:p-3" data-testid="orders-page">
      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5"><ModuleIcon tab="orders" icon={ShoppingCart} /> {tr("Siparişler")}</h2>
          <div className="flex items-center gap-2 zen-panel zen-hc-panel px-3 flex-1 max-w-[420px]">
            <Search className="w-4 h-4 text-slate-400" />
            <input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Ara... (sipariş no, müşteri, proje, form, not)")} className="bg-transparent outline-none text-sm py-2 w-full text-slate-950 dark:text-white placeholder:text-slate-500 dark:placeholder:text-slate-300" />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {widthsDirty && (
            <button onClick={saveColWidths} className="zen-btn-primary !text-xs !py-1.5" data-testid="save-colwidths-btn" title="Ayarladığınız sütun genişliklerini kalıcı yapar">
              <Check className="w-3.5 h-3.5" /> Sütunları Kaydet
            </button>
          )}
          {Object.keys(colWidths).length > 0 && (
            <button onClick={resetColWidths} className="zen-btn-ghost !text-xs !py-1.5" data-testid="reset-colwidths-btn" title="Varsayılan genişliklere dön">
              Sıfırla
            </button>
          )}
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="zen-input !w-auto" data-testid="orders-filter">
            <option value="all">{tr("Tümü")}</option>
            <option value="teklif">{tr("Teklif (Onaylanmamış)")}</option>
            <option value="onay_bekliyor">{tr("Onay Bekliyor")}</option>
            <option value="imalatta">{tr("İmalatta")}</option>
            <option value="kargolanacak">{tr("Kargolanacak")}</option>
            <option value="yollandi">{tr("Yollandı")}</option>
            <option value="teslim_odeme_bekliyor">{tr("Teslim / Ödeme Bekliyor")}</option>
            <option value="odendi">{tr("Ödendi")}</option>
            <option value="tamamlandi">{tr("Tamamlandı")}</option>
            <option value="iptal">{lang === "en" ? "Cancelled" : "İptal"}</option>
            {/* Ayarlar → Satış Durumları'ndan gelen custom durumlar */}
            {customStatuses.length > 0 && <option disabled>─── Özel Durumlar ───</option>}
            {customStatuses.map(s => (
              <option key={s.id} value={s.key}>{s.label || s.value || s.key}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 zen-panel zen-hc-panel px-2 py-1">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="zen-input !w-auto !py-1 !text-xs" data-testid="orders-date-from" title="Başlangıç tarihi" />
            <span className="text-[10px] text-slate-400">→</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="zen-input !w-auto !py-1 !text-xs" data-testid="orders-date-to" title="Bitiş tarihi" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-[10px] text-slate-400 hover:text-[#E5322A] px-1" title="Tarih filtresini temizle">✕</button>
            )}
          </div>
          {!isDealer && (
          <button data-testid="new-order-btn" onClick={() => { setEditingOrder(null); setShowNewOrderQuote(true); }} className="zen-btn-primary !bg-gradient-to-r !from-[#E5322A] !to-[#C42821] !border-transparent !py-[0.467rem] hover:!brightness-110">
            <Plus className="w-4 h-4" /> {tr("Yeni Sipariş Ekle")}
          </button>
          )}
        </div>
      </div>

      <div className="zen-panel zen-hc-panel overflow-hidden">
        {selected.size > 0 && (
          <div className="flex items-center gap-3 px-4 py-2.5 bg-[#E5322A]/8 border-b border-[#E5322A]/20" data-testid="bulk-action-bar">
            <span className="text-sm font-semibold text-[#C42821]">{selected.size} sipariş seçildi</span>
            <div className="flex gap-2 ml-auto">
              <button
                data-testid="bulk-pdf-sade-btn"
                onClick={() => exportBulkPDF("sade")}
                disabled={exportingPdf}
                className="zen-btn-ghost border border-slate-200 dark:border-[#22304A] text-xs !py-1.5"
              >
                <FileDown className="w-3.5 h-3.5" /> {exportingPdf ? "Oluşturuluyor..." : "Özet PDF"}
              </button>
              <button
                data-testid="bulk-pdf-detayli-btn"
                onClick={() => exportBulkPDF("detayli")}
                disabled={exportingPdf}
                className="zen-btn-primary text-xs !py-1.5"
              >
                <Download className="w-3.5 h-3.5" /> Detaylı PDF
              </button>
              <button onClick={() => setSelected(new Set())} className="zen-btn-ghost text-xs !py-1.5 text-slate-400">
                <X className="w-3.5 h-3.5" /> Seçimi Temizle
              </button>
            </div>
          </div>
        )}
        <StickyScrollWrapper>
        <table className="zen-table zen-table--compact zen-table--fixed">
          <thead>
            <tr>
              <th style={{width:"32px"}} className="text-center">
                <input
                  type="checkbox"
                  data-testid="select-all-orders"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onChange={toggleSelectAll}
                  className="rounded cursor-pointer accent-[#E5322A]"
                />
              </th>
              <th style={{width:"6px", padding:0}} />
              <ThF label={tr("Sıra")} k="sira" value={colFilters.sira} onChange={setCF} width={colW.sira} onResize={startResize} />
              <ThF label={tr("Sipariş No")} k="orderNo" value={colFilters.orderNo} onChange={setCF} width={colW.orderNo} onResize={startResize} />
              <ThF label={tr("Durum")} k="status" value={colFilters.status} onChange={setCF} width={colW.status} onResize={startResize} />
              <ThF label={tr("Cari")} k="cari" value={colFilters.cari} onChange={setCF} width={colW.cari} onResize={startResize} />
              <ThF label={tr("Referans")} k="proje" value={colFilters.proje} onChange={setCF} right onResize={startResize} hideClass="zen-col-hide-sm" />
              <ThF label={tr("Form")} k="forms" value={colFilters.forms} onChange={setCF} center width={colW.forms} onResize={startResize} hideClass="zen-col-hide-md" />
              <ThF label={tr("Adet")} k="adet" value={colFilters.adet} onChange={setCF} center width={colW.adet} onResize={startResize} hideClass="zen-col-hide-sm" />
              <ThF label={tr("Tutar")} k="tutar" value={colFilters.tutar} onChange={setCF} right width={colW.tutar} onResize={startResize} />
              <th style={{width:"52px", padding:"0 6px"}} className="text-center zen-col-hide-sm" title="Üretildi / Fatura" />
              <ThF label={tr("İşlem")} k="islem" value={colFilters.islem} onChange={setCF} center width={colW.islem} onResize={startResize} />
            </tr>
          </thead>
          <tbody>
            {paginated.map((o, idx) => {
              const stat = STATUS_LABELS[o.status] || { tr: o.status, color: "slate" };
              const rc = resolveColor(o.status); // özelleştirilmiş veya varsayılan renk
              const productForms = Array.from(new Set((o.items || []).map(it => (it.product || "").split(" ").slice(0,3).join(" ").toUpperCase())));
              const countableItems = (o.items || []).filter((it) => !it.isAccessory);
              const parts = countableItems.reduce((s, it) => s + (Number(it.qty) || 0), 0) || countableItems.length;
              const hasDiscount = !!(o.discountPercent && o.discountPercent > 0);
              const isExpanded = expandedId === o.id;
              return (
                <React.Fragment key={o.id}>
                <tr className="group relative cursor-pointer" onClick={() => setDetailOrder(o)} data-testid={`order-row-${o.id}`}>
                  <td style={{padding:"0 8px"}} className="text-center" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      data-testid={`select-order-${o.id}`}
                      checked={selected.has(o.id)}
                      onChange={() => toggleSelect(o.id)}
                      className="rounded cursor-pointer accent-[#E5322A]"
                    />
                  </td>
                  <td style={{padding:0, width:"6px"}}>
                    <div className={`w-1.5 h-full absolute left-0 top-0 bottom-0 ${STATUS_DOT[rc] || "bg-slate-500"}`} />
                  </td>
                  <td className="text-slate-500 text-xs px-1" onClick={(e) => e.stopPropagation()}>
                    <div className="relative inline-flex items-center gap-0.5">
                      {/* ·· 3-nokta hızlı aksiyon menüsü */}
                      <button
                        data-testid={`quick-menu-btn-${o.id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (quickMenu?.order?.id === o.id) { setQuickMenu(null); return; }
                          const rect = e.currentTarget.getBoundingClientRect();
                          const menuH = 370;
                          const flipUp = rect.bottom + 6 + menuH > window.innerHeight - 8;
                          setQuickMenu({ order: o, top: flipUp ? Math.max(8, rect.top - 6 - menuH) : rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 216) });
                        }}
                        className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-[#22304A] hover:text-slate-700 dark:hover:text-slate-200 transition-colors opacity-0 group-hover:opacity-100"
                        title="Hızlı Aksiyonlar"
                      >
                        <MoreHorizontal className="w-3.5 h-3.5" />
                      </button>
                      {isDealer ? (
                        <span
                          data-testid={`status-dot-${o.id}`}
                          title={stat.tr}
                          className={`w-4 h-4 rounded-full shrink-0 ${STATUS_DOT[rc] || "bg-slate-500"} ring-2 ring-slate-200 dark:ring-[#22304A]`}
                        />
                      ) : (
                        <button
                          data-testid={`status-dot-${o.id}`}
                          title={tr("Durumu Değiştir")}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (statusMenu?.order?.id === o.id) { setStatusMenu(null); return; }
                            const r = e.currentTarget.getBoundingClientRect();
                            const menuH = ORDER_STATUS_FLOW.length * 30 + 44;
                            const flipUp = r.bottom + 6 + menuH > window.innerHeight - 8;
                            setStatusMenu({ order: o, top: flipUp ? Math.max(8, r.top - 6 - menuH) : r.bottom + 6, left: Math.min(r.left, window.innerWidth - 208) });
                          }}
                          className={`w-4 h-4 rounded-full shrink-0 ${STATUS_DOT[rc] || "bg-slate-500"} hover:scale-125 transition-transform ring-2 ring-slate-200 dark:ring-[#22304A]`}
                        />
                      )}
                      <span className="inline-flex items-center gap-1 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : o.id)}>#{idx+1}<ChevronRight className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-90 text-[#E5322A]" : ""}`} /></span>
                    </div>
                  </td>
                  <td className="zen-mono font-bold text-[#E5322A]" style={{width:"110px"}} onClick={(e) => e.stopPropagation()}>
                    {ono(o)}
                    <div className="mt-1"><TailorSheetButton record={o} testId={`order-tailor-sheet-${o.id}`} onClick={() => { setPrintDocType("tulfon"); setPrintOrder(o); }} /></div>
                    {views[o.id]?.viewCount ? (
                      <button
                        onClick={() => setSeenOrder(o)}
                        data-testid={`order-seen-btn-${o.id}`}
                        title="Müşteri PDF'i açtı — detaylar için tıkla"
                        className="mt-0.5 block zen-chip bg-emerald-500/15 text-emerald-600 !text-[9px] hover:bg-emerald-500/25 transition-colors"
                      >
                        görüldü ✓ {new Date(views[o.id].firstViewedAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} · {views[o.id].viewCount} kez
                      </button>
                    ) : null}
                  </td>
                  <td>
                    <span className={`zen-chip whitespace-nowrap !text-[10px] !font-bold !px-2 !py-1 ${STATUS_CHIP[rc] || STATUS_CHIP.slate}`}>{stat.tr}</span>
                    <div className="text-[9px] text-slate-500 mt-0.5">{new Date(o.createdAt || Date.now()).toLocaleString("tr-TR", {day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit"})}</div>
                  </td>
                  <td style={{maxWidth:"215px"}}>
                    <div className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-[#E5322A]/15 text-[#E5322A] flex items-center justify-center text-[8px] font-bold shrink-0">{(o.customerName || "?").slice(0,1).toUpperCase()}</div>
                      <div>
                        <div className="font-semibold text-xs truncate">{o.customerName}</div>
                        {o.createdBy ? <div className="text-[9px] text-slate-400 truncate">👤 {o.createdBy}</div> : null}
                      </div>
                    </div>
                  </td>
                  <td className="text-xs zen-col-hide-sm text-right" title={o.notes || undefined}>
                    {/* REFERANS: yalnızca proje/referans adı — notlar BURADA görünmez (yalnız ipucunda) */}
                    {o.projectName ? <span className="font-semibold truncate block">{o.projectName}</span> : <span className="text-slate-400">—</span>}
                    {o.deadline && <DeadlineBadge order={o} testid={`deadline-badge-${o.id}`} />}
                  </td>
                  <td className="zen-col-hide-md text-center" style={{width:"150px"}}>
                    <div className="flex flex-wrap gap-0.5 justify-center">
                      {productForms.slice(0,2).map(pf => (
                        <span key={pf} className="text-[9px] font-bold tracking-wider text-slate-500 dark:text-slate-400 truncate">{pf}</span>
                      ))}
                    </div>
                  </td>
                  <td className="text-center zen-mono text-sm zen-col-hide-sm" style={{maxWidth:"44px"}}>{parts || countableItems.length}</td>
                  <td className="text-right whitespace-nowrap" style={{width:"140px"}}>
                    <div className="inline-flex flex-col items-end gap-0.5">
                      <div className="zen-mono font-bold text-xs whitespace-nowrap tabular-nums">{fmt(o.total, o.currency)}</div>
                      {hasDiscount && <div className="zen-mono text-[9px] text-slate-500 line-through whitespace-nowrap tabular-nums">{fmt(o.subtotal, o.currency)}</div>}
                      {pageMaxTotal > 1 && (
                        <div className="h-[2px] rounded-full bg-slate-200 dark:bg-[#22304A] overflow-hidden w-[48px]" title="Sayfadaki en büyük siparişe oranla ciro katkısı">
                          <div className="h-full rounded-full bg-[#E5322A]/70" style={{ width: `${Math.min(100, ((Number(o.total) || 0) / pageMaxTotal) * 100)}%` }} />
                        </div>
                      )}
                    </div>
                  </td>
                  {/* ÜRETİLDİ / FATURA rozetleri — ayrı sütun */}
                  <td style={{width:"52px", padding:"0 6px"}} className="zen-col-hide-sm" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-center">
                      {PRODUCED_STATUSES.has(o.status) && (
                        <button
                          data-testid={`badge-uretildi-${o.id}`}
                          onClick={(e) => { e.stopPropagation(); setDetailOrder(o); }}
                          title="Üretildi — Sipariş detayı"
                          className="zen-badge-uretildi w-[18px] h-[18px] rounded flex items-center justify-center bg-teal-500/15 text-teal-700 dark:text-teal-300 border border-teal-500/30 hover:bg-teal-500/25 transition-colors"
                        >
                          <Check className="w-2.5 h-2.5" />
                        </button>
                      )}
                      {orderInvoices[o.id] && (
                        <button
                          data-testid={`badge-fatura-${o.id}`}
                          onClick={(e) => { e.stopPropagation(); setInvoiceViewer(orderInvoices[o.id]); }}
                          title="Fatura kesildi"
                          className="zen-badge-fatura w-[18px] h-[18px] rounded flex items-center justify-center bg-orange-500/15 text-orange-700 dark:text-orange-300 border border-orange-500/30 hover:bg-orange-500/25 transition-colors"
                        >
                          <FileText className="w-2.5 h-2.5" />
                        </button>
                      )}
                      {o.isLegacy && (
                        <span
                          data-testid={`badge-legacy-${o.id}`}
                          title="Bayiloji geçmiş kaydı"
                          className="w-[18px] h-[18px] rounded flex items-center justify-center bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 text-[9px] font-black"
                        >G</span>
                      )}
                    </div>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-center gap-1 opacity-60 group-hover:opacity-100">
                      {/* Göz ikonu kaldırıldı (24.10.2026): satırın herhangi bir yerine tıklanınca detay açılır */}
                      <button data-testid={`order-pay-${o.id}`} onClick={() => setCardPay({ customerId: o.customerId, customerName: o.customerName, orderId: o.id, orderNo: ono(o), amount: Math.max(0, (o.total || 0) - (o.paidAmount || 0)) })} className="p-1.5 hover:bg-[#E5322A]/10 text-[#E5322A] rounded" title="Kredi Kartı ile Ödeme Yap"><CreditCard className="w-4 h-4" /></button>
                      <button data-testid={`print-open-${o.id}`} onClick={() => { setPrintDocType("teklif"); setPrintOrder(o); }} className="p-1.5 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded" title="Yazdır / PDF Çıktı"><Printer className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
                {isExpanded && (
                  <tr key={o.id + "-detail"} className="bg-slate-50 dark:bg-[#0C1220]" data-testid={`order-detail-${o.id}`}>
                    <td colSpan={12} className="p-0">
                      <div className="p-4 border-t-2 border-[#E5322A]/40">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Sipariş Kalemleri ({(o.items || []).length})</div>
                          <div className="flex items-center gap-2">
                            <PrintTemplateMenu
                              page="orders"
                              data={{
                                orderNo: ono(o),
                                customerName: o.customerName,
                                total: o.total,
                                status: o.status,
                                date: o.createdAt || o.approvedAt || "",
                                city: o.city || "",
                                currency: o.currency,
                                note: o.notes || "",
                              }}
                              buttonLabel="Şablon Yazdır"
                            />
                            <button data-testid={`order-edit-${o.id}`} onClick={() => { setEditingOrder(o); setShowEditOrderQuote(true); }} className="zen-btn-primary !text-xs !py-1.5"><Pencil className="w-3 h-3" /> Düzenle</button>
                          </div>
                        </div>
                        <OrderItemsTable order={o} />
                        {o.notes && <div className="mt-3 text-xs text-slate-500"><strong>Not:</strong> {o.notes}</div>}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="text-center text-slate-500 py-8">Kayıt yok</td></tr>
            )}
          </tbody>
        </table>
        <ZenPagination total={filtered.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
        </StickyScrollWrapper>
      </div>
      {cardPay && (
        <CardPayModal
          customerId={cardPay.customerId}
          customerName={cardPay.customerName}
          orderId={cardPay.orderId}
          orderNo={cardPay.orderNo}
          defaultAmount={cardPay.amount}
          onClose={() => setCardPay(null)}
        />
      )}

      {seenOrder && (
        <QuoteViewsModal          refType="order"
          refId={seenOrder.id}
          title={`Sipariş ${ono(seenOrder)}`}
          subtitle={seenOrder.customerName}
          onClose={() => setSeenOrder(null)}
        />
      )}

      {statusMenu && (
        <>
          <div
            className="fixed z-[60] w-52 zen-panel !rounded-xl shadow-2xl overflow-hidden py-1"
            style={{ top: statusMenu.top, left: statusMenu.left }}
            data-testid={`status-menu-${statusMenu.order.id}`}
          >
            <div className="px-3 pt-2 pb-1.5 border-b border-slate-200/60 dark:border-[#2a3a54]">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Sipariş Durumları</span>
            </div>
            {ORDER_STATUS_FLOW.map((k) => {
              const s = STATUS_LABELS[k];
              const cur = statusMenu.order;
              const isCur = cur.status === k;
              return (
                <button
                  key={k}
                  data-testid={`status-opt-${k}-${cur.id}`}
                  onClick={() => {
                    setStatusMenu(null);
                    if (k !== cur.status) { setNoteText(""); setNotePrompt({ order: cur, status: k }); }
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-xs font-medium text-left transition-colors
                    ${isCur
                      ? "bg-slate-100 dark:bg-[#22304A] text-slate-900 dark:text-white font-semibold"
                      : "hover:bg-slate-50 dark:hover:bg-[#1d2b40] text-slate-700 dark:text-slate-300"
                    }`}
                >
                  <span className={`w-3 h-3 rounded-full shrink-0 ${STATUS_DOT[statusColorOverrides[k] || s.color]}`} />
                  <span className="flex-1">{s.tr}</span>
                  {isCur && <Check className="w-3 h-3 text-emerald-500 shrink-0" />}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Hızlı Aksiyon Menüsü (3 nokta) — Apple Glass */}
      {quickMenu && (
        <div
          className="fixed z-[60] w-48 overflow-hidden rounded-xl shadow-xl
            bg-white/90 dark:bg-[#0E1622]/95
            border border-black/[0.08] dark:border-white/[0.07]
            backdrop-blur-2xl"
          style={{ top: quickMenu.top, left: quickMenu.left, boxShadow: "0 8px 40px rgba(0,0,0,0.22), 0 1px 0 rgba(255,255,255,0.5) inset" }}
          data-testid={`quick-menu-${quickMenu.order.id}`}
        >
          {/* Üst: Sipariş numarası başlık */}
          <div className="px-3 pt-2 pb-1.5 border-b border-black/[0.06] dark:border-white/[0.06]">
            <span className="text-[10px] font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">{ono(quickMenu.order)}</span>
          </div>

          {/* Aksiyonlar */}
          <div className="py-0.5">
            <button data-testid={`qm-copy-${quickMenu.order.id}`}
              onClick={() => { navigator.clipboard.writeText(ono(quickMenu.order)); toast.success(`${ono(quickMenu.order)} kopyalandı`); setQuickMenu(null); }}
              className="w-full flex items-center gap-2 px-3 py-1 text-[11px] font-medium text-slate-700 dark:text-slate-200 text-left hover:bg-black/5 dark:hover:bg-white/8 transition-colors">
              <Copy className="w-3 h-3 text-slate-400 shrink-0" /> Sipariş No Kopyala
            </button>
            <button data-testid={`qm-detail-${quickMenu.order.id}`}
              onClick={() => { setDetailOrder(quickMenu.order); setQuickMenu(null); }}
              className="w-full flex items-center gap-2 px-3 py-1 text-[11px] font-medium text-slate-700 dark:text-slate-200 text-left hover:bg-black/5 dark:hover:bg-white/8 transition-colors">
              <Eye className="w-3 h-3 text-slate-400 shrink-0" /> Detayı Gör
            </button>
            {!isDealer && (
              <button data-testid={`qm-edit-${quickMenu.order.id}`}
                onClick={() => { setEditingOrder(quickMenu.order); setShowEditOrderQuote(true); setQuickMenu(null); }}
                className="w-full flex items-center gap-2 px-3 py-1 text-[11px] font-medium text-slate-700 dark:text-slate-200 text-left hover:bg-black/5 dark:hover:bg-white/8 transition-colors">
                <Pencil className="w-3 h-3 text-slate-400 shrink-0" /> Düzenle
              </button>
            )}
            <button data-testid={`qm-cargo-${quickMenu.order.id}`}
              onClick={() => { printCargo(quickMenu.order); setQuickMenu(null); }}
              className="w-full flex items-center gap-2 px-3 py-1 text-[11px] font-medium text-sky-600 dark:text-sky-400 text-left hover:bg-sky-500/8 transition-colors">
              <Truck className="w-3 h-3 shrink-0" /> Kargo Etiketi
            </button>
            {/* Etiket Bas sayfası (49×20 Zebra) — bu sipariş ÖNCEDEN SEÇİLİ açılır (sol menüdeki sayfanın aynısı) */}
            <button data-testid={`qm-labelprint-${quickMenu.order.id}`}
              onClick={() => {
                try { localStorage.setItem("zen_label_preselect_order", quickMenu.order.id); } catch {}
                setGlobalTab("label_print");
                setQuickMenu(null);
              }}
              className="w-full flex items-center gap-2 px-3 py-1 text-[11px] font-medium text-amber-600 dark:text-amber-400 text-left hover:bg-amber-500/8 transition-colors">
              <Tag className="w-3 h-3 shrink-0" /> Etiket Bas (49×20)
            </button>
            {!orderInvoices[quickMenu.order.id] && !isDealer && (
              <button data-testid={`qm-invoice-${quickMenu.order.id}`}
                onClick={() => { setInvoiceEditor(quickMenu.order); setQuickMenu(null); }}
                className="w-full flex items-center gap-2 px-3 py-1 text-[11px] font-medium text-orange-600 dark:text-orange-400 text-left hover:bg-orange-500/8 transition-colors">
                <FileText className="w-3 h-3 shrink-0" /> Fatura Oluştur
              </button>
            )}
            {orderInvoices[quickMenu.order.id] && (
              <button data-testid={`qm-view-invoice-${quickMenu.order.id}`}
                onClick={() => { setInvoiceViewer(orderInvoices[quickMenu.order.id]); setQuickMenu(null); }}
                className="w-full flex items-center gap-2 px-3 py-1 text-[11px] font-medium text-orange-600 dark:text-orange-400 text-left hover:bg-orange-500/8 transition-colors">
                <FileText className="w-3 h-3 shrink-0" /> Faturayı Gör
              </button>
            )}
            <button data-testid={`qm-customer-${quickMenu.order.id}`}
              onClick={() => { setGlobalTab("customers"); setQuickMenu(null); }}
              className="w-full flex items-center gap-2 px-3 py-1 text-[11px] font-medium text-slate-700 dark:text-slate-200 text-left hover:bg-black/5 dark:hover:bg-white/8 transition-colors">
              <Users className="w-3 h-3 text-slate-400 shrink-0" /> Cariyi Gör
            </button>
            {!isDealer && (
              <button data-testid={`qm-archive-${quickMenu.order.id}`}
                onClick={() => { del(quickMenu.order); setQuickMenu(null); }}
                className="w-full flex items-center gap-2 px-3 py-1 text-[11px] font-medium text-rose-500 text-left hover:bg-rose-500/8 transition-colors">
                <Trash2 className="w-3 h-3 shrink-0" /> Arşive Kaldır
              </button>
            )}
          </div>

          {/* Durum Değiştir — sadece ana durumlar */}
          <div className="border-t border-black/[0.06] dark:border-white/[0.06] pt-0.5 pb-0.5">
            <div className="px-3 py-1 flex items-center justify-between">
              <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">Hızlı Durum</span>
              <button onClick={() => { setDetailOrder(quickMenu.order); setQuickMenu(null); }} className="text-[9px] text-[#E5322A] font-semibold hover:underline">Tümü →</button>
            </div>
            {["onay_bekliyor", "imalatta", "kargolanacak", "tamamlandi", "iptal"].map((k) => {
              const s = STATUS_LABELS[k];
              if (!s) return null;
              const isCurrent = quickMenu.order.status === k;
              const dotCls = STATUS_DOT[statusColorOverrides[k] || s.color] || "bg-slate-500";
              return (
                <button
                  key={k}
                  data-testid={`qm-status-${k}-${quickMenu.order.id}`}
                  onClick={() => {
                    setQuickMenu(null);
                    if (k !== quickMenu.order.status) { setNoteText(""); setNotePrompt({ order: quickMenu.order, status: k }); }
                  }}
                  className={`w-full flex items-center gap-2 px-3 py-0.5 text-[10.5px] text-left transition-colors
                    ${isCurrent
                      ? "bg-black/5 dark:bg-white/8 font-semibold text-slate-900 dark:text-white"
                      : "font-medium text-slate-600 dark:text-slate-300 hover:bg-black/5 dark:hover:bg-white/5"
                    }`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${dotCls}`} />
                  <span className="flex-1 truncate">{s.tr}</span>
                  {isCurrent && <Check className="w-2.5 h-2.5 text-emerald-500 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* YENİ SİPARİŞ: QuoteBuilder motoru (aynı form, aynı hesaplama) */}
      {showNewOrderQuote && (
        <QuoteBuilderPage
          saveAsOrder
          onlyRegisteredCustomers
          onClose={() => setShowNewOrderQuote(false)}
          onSaved={() => { setShowNewOrderQuote(false); load(); }}
        />
      )}
      {/* SİPARİŞ FORMU: QuoteBuilder motoru (yeni + düzenleme + CRM lead — hepsi aynı) */}
      {showEditOrderQuote && editingOrder && (
        <QuoteBuilderPage
          saveAsOrder
          onlyRegisteredCustomers
          editingQuote={editingOrder}
          onClose={() => { setShowEditOrderQuote(false); setEditingOrder(null); }}
          onSaved={() => { setShowEditOrderQuote(false); setEditingOrder(null); load(); }}
        />
      )}
      {printOrder && <PrintPreviewModal key={`${printOrder.id}-${printDocType}`} order={printOrder} initialDocType={printDocType} onClose={() => setPrintOrder(null)} />}
      {detailOrder && <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} onEdit={(o) => { setDetailOrder(null); setEditingOrder(o); setShowEditOrderQuote(true); }} onStatusChange={(o, newStatus) => { setNoteText(""); setNotePrompt({ order: o, status: newStatus }); }} onFinancialEdit={(o) => { setFinancialEditOrder(o); }} />}
      {financialEditOrder && <FinancialEditModal order={financialEditOrder} onClose={() => setFinancialEditOrder(null)} onSaved={() => { setFinancialEditOrder(null); setDetailOrder(null); load(); }} />}

      {/* Fatura Hızlı Görüntüleyici */}
      {invoiceViewer && <InvoiceQuickView invoice={invoiceViewer} onClose={() => setInvoiceViewer(null)} />}
      {/* Fatura Editörü */}
      {invoiceEditor && <InvoiceEditorModal order={invoiceEditor} onClose={() => setInvoiceEditor(null)} onSaved={() => { setInvoiceEditor(null); load(); }} />}

      {/* Durum Değişikliği Notu Modalı */}
      {notePrompt && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="status-note-modal">
          <div className="zen-panel w-full max-w-sm p-5 zen-anim-up">
            <h3 className="font-bold text-sm mb-1">Durum Değişikliği</h3>
            <p className="text-xs text-slate-500 mb-3">
              <span className="zen-mono font-bold text-[#E5322A]">{ono(notePrompt.order)}</span>
              {" — "}{(STATUS_LABELS[notePrompt.order.status] || { tr: notePrompt.order.status }).tr}
              {" → "}<b>{(STATUS_LABELS[notePrompt.status] || { tr: notePrompt.status }).tr}</b>
            </p>
            <textarea
              data-testid="status-note-input"
              className="zen-input w-full !text-xs"
              rows={3}
              placeholder="Not (opsiyonel) — boş bıraksanız da tarih otomatik kaydedilir"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button className="zen-btn-ghost !text-xs" data-testid="status-note-cancel" onClick={() => { setNotePrompt(null); setNoteText(""); }}>Vazgeç</button>
              <button className="zen-btn-primary !text-xs" data-testid="status-note-save" onClick={applyStatusChange}>Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Fatura Hızlı Görüntüleyici Modal ─────────────────────────────────────────
function InvoiceQuickView({ invoice: inv, onClose }) {
  const { tr } = useContext(UICtx);
  const fmt = (v, c) => {
    if (v == null) return "—";
    try { return Number(v).toLocaleString("tr-TR", { style: "currency", currency: c || "TRY", minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
    catch { return `${Number(v).toFixed(2)} ${c || "₺"}`; }
  };
  const typeMap = { satis: "Satış Faturası", iade: "İade Faturası", alis: "Alış Faturası" };
  const catMap = { e_fatura: "e-Fatura", e_arsiv: "e-Arşiv", kagit: "Kâğıt Fatura" };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-5 overflow-y-auto" onClick={onClose} data-testid="invoice-viewer-overlay">
      <div className="zen-panel w-full max-w-2xl zen-anim-up overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="invoice-viewer-modal">
        {/* Başlık */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-[#22304A]">
          <div>
            <div className="flex items-center gap-2">
              <span className="zen-mono font-black text-xl text-orange-500" data-testid="invoice-no">{inv.docNo || inv.invoiceNo || "—"}</span>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-600 dark:text-orange-400">{typeMap[inv.type] || inv.type}</span>
              {inv.invoiceCategory && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-slate-500/15 text-slate-600 dark:text-slate-400">{catMap[inv.invoiceCategory] || inv.invoiceCategory}</span>}
            </div>
            <div className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">{inv.customerName}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {inv.issueDate && `Tarih: ${new Date(inv.issueDate).toLocaleDateString("tr-TR")} · `}
              {inv.dueDate && `Vade: ${new Date(inv.dueDate).toLocaleDateString("tr-TR")}`}
            </div>
          </div>
          <button data-testid="invoice-viewer-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] transition-colors">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Kalemler */}
          {(inv.items || []).length > 0 && (
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Fatura Kalemleri</div>
              <div className="zen-panel !p-0 overflow-hidden">
                <table className="zen-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Ürün / Açıklama</th>
                      <th className="text-center">Miktar</th>
                      <th className="text-right">Birim Fiyat</th>
                      <th className="text-right">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(inv.items || []).map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-slate-400 text-[11px]">{idx + 1}</td>
                        <td className="font-semibold text-[12px]">{item.description || item.product || "—"}</td>
                        <td className="text-center zen-mono text-[11px]">{item.qty ?? item.quantity ?? "—"} {item.unit && <span className="text-slate-400">{item.unit}</span>}</td>
                        <td className="text-right zen-mono text-[11px]">{item.unitPrice != null ? fmt(item.unitPrice, inv.currency) : "—"}</td>
                        <td className="text-right zen-mono font-bold text-[12px]">{item.lineTotal != null ? fmt(item.lineTotal, inv.currency) : (item.unitPrice && item.qty ? fmt(item.unitPrice * item.qty, inv.currency) : "—")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Finansal Özet */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="zen-panel p-4 space-y-2 text-[12px]">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Finansal Özet</div>
              <div className="flex justify-between"><span className="text-slate-500">Ara Toplam</span><span className="zen-mono">{fmt(inv.subtotal, inv.currency)}</span></div>
              {(inv.vatBreakdown || []).map((v, i) => (
                <div key={i} className="flex justify-between text-slate-400 text-[11px]"><span>KDV %{v.rate}</span><span className="zen-mono">{fmt(v.kdv, inv.currency)}</span></div>
              ))}
              {inv.vatAmount > 0 && !(inv.vatBreakdown || []).length && (
                <div className="flex justify-between text-slate-400 text-[11px]"><span>KDV</span><span className="zen-mono">{fmt(inv.vatAmount, inv.currency)}</span></div>
              )}
              <div className="flex justify-between font-bold text-base pt-1.5 border-t border-slate-200 dark:border-[#22304A]">
                <span>Genel Toplam</span>
                <span className="zen-mono text-orange-500">{fmt(inv.total, inv.currency)}</span>
              </div>
            </div>

            {(inv.notes || inv.orderId) && (
              <div className="zen-panel p-4 text-[12px]">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Bilgi</div>
                {inv.orderId && (
                  <div className="flex items-center gap-1.5 mb-2">
                    <ShoppingCart className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-slate-500">Bağlı Sipariş:</span>
                    <span className="zen-mono font-bold text-[#E5322A]">#{inv.orderId.slice(-6).toUpperCase()}</span>
                  </div>
                )}
                {inv.notes && <div className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{inv.notes}</div>}
              </div>
            )}
          </div>

          {/* Yazdır */}
          <div className="flex justify-end">
            <button data-testid="invoice-print-btn" onClick={() => window.print()} className="zen-btn-ghost !text-xs flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> Yazdır / İndir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
